/* Lunafreya Glass Header — Mobile menu cascade (sub-screens, back navigation). */
(() => {
  const KEY = '__LunafreyaMobileCascade';
  const ROOT = '[data-luna-header-v25]';
  if (window[KEY]) {
    window[KEY].init(document);
    return;
  }

  class LunaMobileCascade {
    constructor(root) {
      if (!root || root._lunaMobileCascade) return;
      root._lunaMobileCascade = this;
      this.root = root;
      this.panel = root.querySelector('[data-luna-panel="menu"]');
      this.shell = root.querySelector('[data-luna-mobile-shell]');
      if (!this.panel || !this.shell) return;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.screens = new Map([...this.shell.querySelectorAll('[data-luna-mobile-screen]')].map(screen => [screen.dataset.lunaMobileScreen, screen]));
      this.stack = [];
      this.positions = new Map();
      this.animating = false;
      this.pointerStart = null;
      this.motion = this.getMotion();
      this.bind();
      this.reset(false);
    }

    getMotion() {
      const raw = parseFloat(getComputedStyle(this.root).getPropertyValue('--luna-motion')) || 420;
      return Math.max(220, Math.min(760, raw + 100));
    }

    bind() {
      this.root.addEventListener('click', event => this.onClick(event), { signal: this.signal });
      this.root.addEventListener('keydown', event => this.onKeydown(event), { signal: this.signal, capture: true });
      this.shell.addEventListener('pointerdown', event => this.onPointerDown(event), { signal: this.signal, passive: true });
      this.shell.addEventListener('pointerup', event => this.onPointerUp(event), { signal: this.signal, passive: true });
      this.shell.addEventListener('pointercancel', () => { this.pointerStart = null; }, { signal: this.signal, passive: true });
      document.addEventListener('shopify:block:select', event => this.onBlockSelect(event), { signal: this.signal });
    }

    onClick(event) {
      const panelOpen = event.target.closest('[data-luna-open="menu"]');
      if (panelOpen && this.root.contains(panelOpen)) {
        window.setTimeout(() => {
          this.reset(true);
          this.focusCurrent('[data-luna-mobile-close]');
        }, 90);
        return;
      }
      const opener = event.target.closest('[data-luna-mobile-open]');
      if (opener && this.panel.contains(opener)) {
        event.preventDefault();
        this.open(opener.dataset.lunaMobileOpen, opener);
        return;
      }
      const back = event.target.closest('[data-luna-mobile-back]');
      if (back && this.panel.contains(back)) {
        event.preventDefault();
        this.back();
        return;
      }
      const close = event.target.closest('[data-luna-close]');
      if (close && this.panel.contains(close)) {
        window.setTimeout(() => { if (!this.panel.classList.contains('is-open')) this.reset(true); }, this.motion + 40);
      }
    }

    onKeydown(event) {
      if (this.panel.hidden || !this.panel.classList.contains('is-open')) return;
      if ((event.key === 'Escape' || event.key === 'ArrowLeft') && this.stack.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.back();
      }
    }

    onPointerDown(event) {
      if (!this.stack.length || this.animating || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.pointerStart = { x: event.clientX, y: event.clientY, time: performance.now(), id: event.pointerId };
    }

    onPointerUp(event) {
      const start = this.pointerStart;
      this.pointerStart = null;
      if (!start || start.id !== event.pointerId || !this.stack.length) return;
      const dx = event.clientX - start.x;
      const dy = Math.abs(event.clientY - start.y);
      const elapsed = performance.now() - start.time;
      if (dx > 72 && dx > dy * 1.35 && elapsed < 900) this.back();
    }

    current() {
      return this.shell.querySelector('[data-luna-mobile-screen].is-active');
    }

    scrollNode(screen) {
      return screen?.querySelector('[data-luna-mobile-scroll]');
    }

    savePosition(screen) {
      const scroller = this.scrollNode(screen);
      if (screen && scroller) this.positions.set(screen.dataset.lunaMobileScreen, scroller.scrollTop);
    }

    restorePosition(screen) {
      const scroller = this.scrollNode(screen);
      if (!screen || !scroller) return;
      scroller.scrollTop = this.positions.get(screen.dataset.lunaMobileScreen) || 0;
    }

    setExpanded(key, expanded) {
      this.panel.querySelectorAll(`[data-luna-mobile-open="${CSS.escape(key)}"]`).forEach(button => button.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
    }

    open(key, trigger) {
      if (this.animating) return;
      const next = this.screens.get(key);
      const current = this.current();
      if (!next || !current || next === current) return;
      this.animating = true;
      this.savePosition(current);
      this.stack.push(current.dataset.lunaMobileScreen);
      next.hidden = false;
      next.setAttribute('aria-hidden', 'false');
      next.className = 'luna-mobile-screen is-preparing';
      current.setAttribute('aria-hidden', 'true');
      this.setExpanded(key, true);
      this.restorePosition(next);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        next.classList.remove('is-preparing');
        next.classList.add('is-active');
      }));
      window.setTimeout(() => {
        current.hidden = true;
        current.classList.remove('is-leaving');
        this.animating = false;
        this.focusCurrent('[data-luna-mobile-back]');
      }, this.motion);
    }

    back() {
      if (this.animating || !this.stack.length) return;
      const current = this.current();
      const previousKey = this.stack.pop();
      const previous = this.screens.get(previousKey);
      if (!current || !previous) return;
      this.animating = true;
      this.savePosition(current);
      previous.hidden = false;
      previous.setAttribute('aria-hidden', 'false');
      previous.className = 'luna-mobile-screen is-preparing-left';
      current.setAttribute('aria-hidden', 'true');
      this.setExpanded(current.dataset.lunaMobileScreen, false);
      this.restorePosition(previous);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        previous.classList.remove('is-preparing-left');
        previous.classList.add('is-active');
        current.classList.remove('is-active');
        current.classList.add('is-exiting-right');
      }));
      window.setTimeout(() => {
        current.hidden = true;
        current.classList.remove('is-exiting-right');
        this.animating = false;
        this.focusCurrent(previousKey === 'root' ? '.luna-mobile-main-link' : '[data-luna-mobile-back]');
      }, this.motion);
    }

    reset(preserveRoot = true) {
      const rootScreen = this.screens.get('root');
      if (!rootScreen) return;
      this.stack = [];
      this.animating = false;
      this.panel.querySelectorAll('[data-luna-mobile-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.screens.forEach((screen, key) => {
        screen.className = 'luna-mobile-screen';
        screen.hidden = key !== 'root';
        screen.setAttribute('aria-hidden', key === 'root' ? 'false' : 'true');
      });
      rootScreen.classList.add('is-active');
      const scroller = this.scrollNode(rootScreen);
      if (scroller) scroller.scrollTop = preserveRoot ? (this.positions.get('root') || 0) : 0;
    }

    focusCurrent(selector) {
      window.setTimeout(() => {
        const screen = this.current();
        const target = screen?.querySelector(selector) || screen?.querySelector('button,a[href]');
        target?.focus({ preventScroll: true });
      }, 70);
    }

    onBlockSelect(event) {
      const detail = event.detail || {};
      if (detail.sectionId && String(detail.sectionId) !== String(this.root.dataset.sectionId)) return;
      const id = String(detail.blockId || '');
      if (!id) return;
      const mobileBanner = this.root.querySelector(`[data-luna-mobile-banner-block="${CSS.escape(id)}"]`);
      if (mobileBanner) {
        const screen = mobileBanner.closest('[data-luna-mobile-screen]');
        const instance = this.root._lunafreya;
        instance?.openPanel?.('menu');
        this.reset(false);
        if (screen && screen.dataset.lunaMobileScreen !== 'root') {
          window.setTimeout(() => this.open(screen.dataset.lunaMobileScreen), 100);
        } else {
          window.setTimeout(() => mobileBanner.scrollIntoView({ block: 'center', behavior: 'smooth' }), 120);
        }
        return;
      }
      const showcase = this.root.querySelector(`[data-luna-desktop-showcase-block="${CSS.escape(id)}"]`);
      if (showcase) {
        const view = showcase.closest('[data-luna-editorial-view]');
        const instance = this.root._lunafreya;
        if (view && instance) {
          instance.activateEditorial?.(view.dataset.lunaEditorialView);
          instance.openPanel?.('editorial');
          window.setTimeout(() => showcase.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 120);
        }
      }
    }

    destroy() {
      this.abort?.abort();
      delete this.root._lunaMobileCascade;
    }
  }

  const manager = {
    init(scope = document) {
      const roots = scope.matches?.(ROOT) ? [scope] : [...scope.querySelectorAll(ROOT)];
      roots.forEach(root => {
        if (!root._lunaMobileCascade) new LunaMobileCascade(root);
      });
    },
    destroy(scope) {
      const root = scope?.matches?.(ROOT) ? scope : scope?.querySelector?.(ROOT);
      root?._lunaMobileCascade?.destroy();
    }
  };

  window[KEY] = manager;
  document.addEventListener('shopify:section:load', event => manager.init(event.target));
  document.addEventListener('shopify:section:unload', event => manager.destroy(event.target));
  manager.init(document);
})();
